<template>
  <div>
    <div
      ref="containerEl"
      class="flowbuilder-node-container flowbuilder-widgets"
    >
      <!-- System Flow - shows "When a page is viewed" instead of widget association -->
      <BaseNode
        v-if="isSystemFlow || templatePreview"
        ref="nodeEl"
        classes="bg-stone-100 dark:bg-stone-950 ring-stone-400/50 dark:ring-stone-700/50"
        :node="node"
        :icon="BoltIcon"
        :hide-menu="true"
      >
        <template #context>
          Trigger flow:
        </template>
        When a page is viewed
      </BaseNode>

      <!-- Regular Flow - shows widget association UI -->
      <template v-else>
        <!-- Warning slot for webapp to show widget association warning -->
        <div
          v-if="associatedWidgets.length === 0 && !preview"
          class="bg-yellow-100 dark:bg-yellow-950 p-3 rounded-lg text-sm text-brand-text/70 dark:text-white/80 mb-8"
        >
          This Flow won't run without first attaching it to a widget.
        </div>

        <div class="relative flex items-center space-x-12 mb-4">
          <BaseNode
            v-for="widget in associatedWidgets"
            :key="widget.id"
            ref="nodeEl"
            :link-id="widget.id"
            :class="{ 'opacity-50': !widget.isEnabled }"
            classes="bg-stone-100 dark:bg-stone-950 ring-stone-400/50 dark:ring-stone-700/50 h-22 items-center"
            :node="node"
            :icon="BoltIcon"
            :delete-label="'Detach from flow'"
            :needs-setup="!widget.isEnabled"
            setup-label="Disabled"
            hide-duplicate
            @delete-node="handleDetachFromFlow(widget)"
          >
            <div class="flex items-center space-x-2">
              <img
                :src="widgetImagePath(widget)"
                class="w-8 h-8"
              >
              <div class="space-y-1">
                <div class="text-sm font-bold truncate max-w-[13rem]">
                  {{ widget.name }}
                </div>
                <div class="text-xs text-brand-text/70 dark:text-white/80">
                  {{ widgetType(widget.type) }}
                </div>
              </div>
            </div>
          </BaseNode>

          <!-- Associated Pages (use fake node without _uuid so BaseNode uses div, not router-link) -->
          <BaseNode
            v-for="page in associatedPages"
            :key="page.pid"
            ref="nodeEl"
            :class="{ 'opacity-50': page.status === 'draft' }"
            classes="bg-stone-100 dark:bg-stone-950 ring-stone-400/50 dark:ring-stone-700/50 h-22 items-center cursor-pointer"
            :node="{ type: 'page' }"
            :icon="DocumentIcon"
            :hide-menu="true"
            @click="handlePageClick(page)"
          >
            <div class="flex items-center space-x-2">
              <div class="p-1.5 shadow rounded-full bg-white dark:bg-slate-700 dark:text-white/90 w-8">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                  class="w-5 opacity-70"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <div class="space-y-1">
                <div class="text-sm font-bold truncate max-w-[13rem]">
                  {{ page.name }}
                </div>
                <div class="text-xs text-brand-text/70 dark:text-white/80">
                  Page
                </div>
              </div>
            </div>
          </BaseNode>

          <BaseNode
            v-if="!preview || associatedWidgets.length === 0"
            ref="nodeEl"
            :link-id="'wdg_new'"
            classes="bg-stone-100 dark:bg-stone-950 ring-stone-400/50 dark:ring-stone-700/50 opacity-50 transition-opacity duration-200 hover:opacity-100 h-22 items-center"
            :node="node"
            :icon="BoltIcon"
            :hide-menu="true"
          >
            <div class="flex items-center space-x-4 py-1">
              <div class="p-1.5 shadow rounded-full bg-white dark:bg-slate-700 dark:text-white/90 w-8">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                  aria-hidden="true"
                  data-slot="icon"
                  class="w-5 opacity-70"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M12 4.5v15m7.5-7.5h-15"
                  />
                </svg>
              </div>
              <div class="text-base font-bold text-brand-text dark:text-white/80">
                Create new widget
              </div>
            </div>
          </BaseNode>
        </div>
      </template>
    </div>

    <NodeRun
      :nodes="node.child"
      :show-final-dropzone="true"
      :parent-node="node"
      @nodes-updated="handleNodesUpdated"
      @node-action="handleNodeAction"
    />

    <!-- Exit flow node shown for both regular and system flows with appropriate text -->
    <div
      v-if="showFinalExitNode"
      class="flowbuilder-node-container"
    >
      <div class="relative">
        <BaseNode
          ref="nodeEl"
          classes="bg-stone-200 dark:bg-stone-950 ring-stone-700/40 dark:ring-stone-700/40 does-terminate"
          :node="node"
          :icon="BoltIcon"
          :hide-menu="true"
        >
          <template #context>
            {{ isSystemFlow ? 'End of flow:' : 'Exit flow:' }}
          </template>
          {{ isSystemFlow ? 'Flow execution complete' : 'If reached, nothing is asked or pitched!' }}
        </BaseNode>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, inject } from 'vue'
import BaseNode from './BaseNode.vue'
import NodeRun from './NodeRun.vue'
import { FunnelDefinitionRootNode, FunnelDefinitionNode } from '../../types'
import { doesNodeTerminate } from '../../services'

// Simple bolt icon component
const BoltIcon = {
  template: `<svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
  </svg>`
}

// Document icon for pages
const DocumentIcon = {
  template: `<svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>`
}

const props = defineProps<{
  node: FunnelDefinitionRootNode
}>()

const nodeEl = ref<InstanceType<typeof BaseNode> | null>(null)
const containerEl = ref<HTMLElement | null>(null)

const preview = inject('preview', false)
const templatePreview = inject('templatePreview', false)
const adapter = inject('adapter') as any
const flow = inject('flow')
const widgets = inject('widgets')
const pages = inject('pages')

const widgetImagePath = (widget) => {
  return `/img/widget/type/${widget.type}.svg`
}

const widgetType = (type: string) => {
  return {
    'bar': 'Bar',
    'slideup': 'Slideup',
    'toaster': 'Toaster',
    'inline': 'Embedded',
    'modal': 'Popup',
    'takeover': 'Fullscreen'
  }[type]
}

const showFinalExitNode = computed(() => {
  const lastChild = props.node.child[props.node.child.length - 1]
  if (!lastChild) return true

  return !doesNodeTerminate(lastChild)
})

// Handle nodes update from NodeRun
const handleNodesUpdated = (newNodes: FunnelDefinitionNode[]) => {
  // Update the child nodes directly
  props.node.child = newNodes
}

// Handle node actions from NodeRun
const handleNodeAction = (index: number, action: string, data?: any) => {
  const targetNode = props.node.child[index]
  if (!targetNode) return

  if (action === 'click' && adapter?.onNodeClick) {
    adapter.onNodeClick(targetNode)
  } else if (action === 'delete' && adapter?.onNodeDelete) {
    adapter.onNodeDelete(targetNode)
  }
}

const associatedWidgets = computed(() =>
  widgets.value.filter((widget: any) => widget.contentId === flow.value.id)
)

const associatedPages = computed(() =>
  pages.value.filter((page: any) => page.content?.flowId === flow.value.id)
)

const isSystemFlow = computed(() => flow.value?.isSystemFlow === true)

const handlePageClick = (page: any) => {
  if (adapter?.onPageClick) {
    adapter.onPageClick(page)
  }
}

const confirm = inject('confirm')
const handleDetachFromFlow = (widget: any) => {
  confirm.require({
    header: 'Really Detach?',
    message: `This will set this widget to no longer run this flow, and you'll need to re-add it to another flow.`,
    acceptProps: { label: 'Detach', severity: 'danger' },
    rejectProps: { label: 'Cancel', outlined: true },
    accept: () => {
      widget.contentId = null
    }
  })
}

</script>
